import { createAutocomplete } from "@algolia/autocomplete-core";
import type { AutocompleteSource, AutocompleteState, BaseItem } from "@algolia/autocomplete-core";
import { createQuerySuggestionsPlugin } from "@algolia/autocomplete-plugin-query-suggestions";
import { createLocalStorageRecentSearchesPlugin } from "@algolia/autocomplete-plugin-recent-searches";
import { getAlgoliaResults } from "@algolia/autocomplete-preset-algolia";
import { useAlgoliaSearchClient } from "@lib/wrappers/algolia/algoliaSearchClient";
import { getAlgoliaUserToken } from "@lib/analytics/getAlgoliaUserToken";
import { CommonCMS } from "@lib/constants/contentful";
import { getPagePathByLocale, searchPage } from "@lib/constants/pagePaths";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { resolveProductDisplayName } from "@lib/product/productNamePolicy";
import { useStore } from "@store/index";
import { useEffect, useMemo, useRef, useState } from "react";
import { debouncePromise } from "./debouncedUtil";
import { useLocale } from "next-intl";
import { useGlobalModalContext } from "@components/GlobalModalProvider/GlobalModalProvider";
import { usePathname } from "next/navigation";
import { useRouter } from "@i18n/routing";
import { AnalyticsClient } from "@lib/analytics/AnalyticsClient";
import { SearchAnalyticsType } from "@lib/analytics/analyticsTypes";
import type { AlgoliaProduct, AlgoliaSearchClient } from "@lib/wrappers/algolia/types";
import type { Dispatch, RefObject, SetStateAction } from "react";

const DEBOUNCE_MS = 450;
export const debounced = debouncePromise(
    (items: AutocompleteSource<BaseItem>[]) => Promise.resolve(items),
    DEBOUNCE_MS
);
interface UseSearchBarProps {
    searchBarOpen?: boolean;
    isDesktopView?: boolean;
}
type CustomAutoCompleteState = AutocompleteState<BaseItem> & { totalHits?: number };

// Written out explicitly, otherwise TS can't name searchClient's type (LiteClient) because
// of how pnpm nests packages.
type UseSearchBarResult = {
    autocomplete: ReturnType<typeof createAutocomplete<BaseItem>>;
    setAutocompleteState: Dispatch<SetStateAction<CustomAutoCompleteState>>;
    recentSearchesPlugin: ReturnType<typeof createLocalStorageRecentSearchesPlugin>;
    querySuggestionsPlugin: ReturnType<typeof createQuerySuggestionsPlugin>;
    containerRef: RefObject<null>;
    formRef: RefObject<null>;
    inputRef: RefObject<HTMLInputElement | null>;
    isLoading: boolean;
    setIsLoading: Dispatch<SetStateAction<boolean>>;
    searchClient: AlgoliaSearchClient;
    autocompleteState: CustomAutoCompleteState;
};

export const useSearchBar = ({
    searchBarOpen,
    isDesktopView,
}: UseSearchBarProps): UseSearchBarResult => {
    const { push } = useRouter();
    const pathname = usePathname();
    const locale = useLocale();
    const { searchClient, indexName } = useAlgoliaSearchClient(locale);
    const [autocompleteState, setAutocompleteState] = useState<CustomAutoCompleteState>({
        isOpen: false,
        collections: [],
        query: "",
        completion: null,
        totalHits: undefined,
        context: {},
        activeItemId: undefined,
        status: "idle",
    });
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { get: getMicrocopy } = useMicrocopy();
    const { hideModal } = useGlobalModalContext();
    const containerRef = useRef(null);
    const formRef = useRef(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const searchPagePath = useMemo(() => {
        return getPagePathByLocale(searchPage, locale);
    }, [locale]);
    useEffect(() => {
        if (!containerRef.current) {
            return undefined;
        }
    }, []);
    useEffect(() => {
        if (isDesktopView) {
            if (searchBarOpen) {
                inputRef?.current?.focus();
                setAutocompleteState((prevState) => ({
                    ...prevState,
                    isOpen: true,
                }));
            } else {
                inputRef?.current?.blur();
                setAutocompleteState((prevState) => ({
                    ...prevState,
                    isOpen: false,
                }));
            }
        }
    }, [searchBarOpen, isDesktopView]);

    useEffect(() => {
        if (isLoading) {
            setIsLoading(false);
            hideModal?.();
        }
    }, [pathname, isLoading]);

    const recentSearchesPlugin = createLocalStorageRecentSearchesPlugin({
        key: "RECENT_SEARCH",
        limit: 5,
        transformSource({ source }) {
            return {
                ...source,
                getItemUrl({ item }) {
                    return `${searchPagePath}?query=${encodeURIComponent(item.label)}`;
                },
            };
        },
    });
    const currentUser = useStore((state) => state.data.me);
    const userToken = getAlgoliaUserToken(currentUser);
    const querySuggestionsPlugin = createQuerySuggestionsPlugin({
        searchClient,
        indexName: `${indexName}_query_suggestions`,
        getSearchParams() {
            return recentSearchesPlugin?.data?.getAlgoliaSearchParams({
                length: 5,
                offset: 0,
                highlightPreTag: "<mark>",
                highlightPostTag: "</mark>",
                userToken: userToken,
            });
        },
    });
    const autocomplete = useMemo(
        () =>
            createAutocomplete({
                id: "search-autocomplete",
                placeholder: getMicrocopy(CommonCMS.shared, CommonCMS.searchBarPlaceholder),
                openOnFocus: true,
                autoFocus: true,
                plugins: [recentSearchesPlugin, querySuggestionsPlugin],
                initialState: autocompleteState,

                onStateChange({ state }) {
                    const { context = {}, ...rest } = state;
                    setAutocompleteState((prev) => ({
                        ...prev,
                        ...rest,
                        context,
                        totalHits: context.totalHits as number,
                    }));
                },
                stallThreshold: 1000,
                onSubmit(params) {
                    setIsLoading(true);
                    setAutocompleteState((prev) => ({
                        ...prev,
                        isOpen: false,
                    }));
                    params?.setQuery(params.state.query);
                    const q = params?.state?.query?.trim();
                    if (q) {
                        AnalyticsClient.trackSearch(q, SearchAnalyticsType.normal);
                    }
                    const searchUrl = q
                        ? `${searchPagePath}?query=${encodeURIComponent(q)}`
                        : searchPagePath;
                    push(searchUrl);
                    params?.refresh();
                },
                getSources({ query, setContext }) {
                    if (!query) return [];

                    return debounced([
                        {
                            sourceId: "products",
                            getItemInputValue({ item }) {
                                return item?.query as string;
                            },
                            getItems({ query }) {
                                return getAlgoliaResults<AlgoliaProduct & BaseItem>({
                                    searchClient,
                                    queries: [
                                        {
                                            indexName,
                                            // v5 wraps search params in `params` (query included)
                                            params: {
                                                query,
                                                hitsPerPage: 3,
                                                facetFilters: ["hasStock:true"],
                                                userToken: userToken,
                                                clickAnalytics: true,
                                            },
                                        },
                                    ],
                                    transformResponse({ results, hits }) {
                                        const firstResult = results?.[0];
                                        setContext({
                                            totalHits:
                                                firstResult && "nbHits" in firstResult
                                                    ? firstResult.nbHits
                                                    : undefined,
                                        });
                                        return hits.map((group) =>
                                            group.map((hit) => ({
                                                ...hit,
                                                name: resolveProductDisplayName(hit),
                                            }))
                                        );
                                    },
                                });
                            },
                        },
                    ]);
                },
                // @ts-expect-error Autocomplete's container type excludes a nullable React ref.
                container: formRef?.current,
            }),

        [userToken, isDesktopView]
    );

    return {
        autocomplete,
        setAutocompleteState,
        recentSearchesPlugin,
        querySuggestionsPlugin,
        containerRef,
        formRef,
        inputRef,
        isLoading,
        setIsLoading,
        searchClient,
        autocompleteState,
    };
};
